import React, { useRef } from 'react';
import styled from 'styled-components';
import { SaveRounded, Settings as SettingsIcon } from '@material-ui/icons';
import { Box, IconButton, Menu, MenuItem, Paper, Popper, Tooltip, useTheme, useMediaQuery } from '@material-ui/core';
import ColumnSelect from './ColumnSelect';
import exportCSV from './exportCSV';
import exportXLSX from './exportXLSX';
import exportPDF from './exportPDF';
import responsive from '../../utilities/responsive';
import { useScope } from '@/hooks/useScope';

const Header = styled.div`
  display: flex;
  ${p =>
    p.showColumnSelection
      ? ''
      : `
    flex-direction: row-reverse;
  `}

  //> div {
  //  width: 100%;
  //  margin-right: 2em;
  //}
  //> button {
  //  min-width: 8em;
  //}

  ${responsive.md.andSmaller`
    flex-direction: column;
    > div {
      width: 100%;
      margin-bottom: 1em;
    }
  `};
`;

export default ({
  showColumnSelection,
  showCSVExport,
  selectedColumns,
  setSelectedColumns,
  columns,
  columnOptions,
  list,
  data,
  children,
  customExportCSV,
  customExportXLSX,
  customExportPDF,
  exportFileName,
  HeaderLeft,
}) => {
  const scope = useScope();

  if (!(showCSVExport || showColumnSelection || HeaderLeft)) {
    return null;
  }

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState(null);
  const buttonRef = useRef(null);

  const handleSettingsClick = event => {
    if (openSettings) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleExportClick = event => {
    setExportMenuAnchor(exportMenuAnchor ? null : event.currentTarget);
  };

  const exportColumns = React.useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return columns;
    }
    const orderedVisibleTitles = selectedColumns.filter(_ => _.visible).map(_ => _.title);
    const byTitle = new Map(columns.map(c => [c.title, c]));
    return orderedVisibleTitles.map(t => byTitle.get(t)).filter(Boolean);
  }, [selectedColumns, columns]);

  const handleExportOptionClick = format => {
    setExportMenuAnchor(null);
    if (format === 'csv') {
      if (customExportCSV) {
        customExportCSV(list, columns, data, scope, exportFileName);
      } else {
        exportCSV(list, columns, data, scope);
      }
    } else if (format === 'xlsx') {
      if (customExportXLSX) {
        customExportXLSX(list, columns, data, scope, exportFileName);
      } else {
        exportXLSX(list, columns, data, scope);
      }
    } else if (format === 'pdf') {
      if (customExportPDF) {
        customExportPDF(list, columns, data, scope, {
          mode: 'print',
          freezeCols: 1,
          maxColsPerPage: 12,
          fileName: exportFileName ? `${exportFileName}.pdf` : 'export.pdf',
        });
      } else {
        exportPDF(list, exportColumns, data, scope, { mode: 'print', freezeCols: 1, maxColsPerPage: 12 });
      }
    }
  };

  const openSettings = Boolean(anchorEl);
  const openExportMenu = Boolean(exportMenuAnchor);
  const idSettings = openSettings ? 'settings-popper' : undefined;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Header showColumnSelection={showColumnSelection || HeaderLeft}>
      {HeaderLeft && (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          {HeaderLeft}
        </Box>
      )}
      <Box display="flex" alignItems="center" ml="auto">
        {children}
        {showCSVExport && (
          <Box>
            <Tooltip title="Export">
              <IconButton color="primary" size="small" onClick={handleExportClick}>
                <SaveRounded />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={exportMenuAnchor} open={openExportMenu} onClose={() => setExportMenuAnchor(null)}>
              <MenuItem onClick={() => handleExportOptionClick('csv')}>Export as CSV</MenuItem>
              <MenuItem onClick={() => handleExportOptionClick('xlsx')}>Export as XLSX</MenuItem>
              <MenuItem onClick={() => handleExportOptionClick('pdf')}>Print as PDF</MenuItem>
            </Menu>
          </Box>
        )}
        {showColumnSelection && matches && (
          <Box ml={0.5}>
            <Tooltip title="Configure Columns">
              <IconButton size="small" onClick={handleSettingsClick} ref={buttonRef}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Popper style={{ zIndex: 2 }} id={idSettings} open={openSettings} anchorEl={anchorEl}>
              <Paper>
                <ColumnSelect
                  keepOpen
                  options={columnOptions}
                  values={selectedColumns}
                  onChange={c => setSelectedColumns(c)}
                  onClose={handleCloseSettings}
                  buttonRef={buttonRef}
                />
              </Paper>
            </Popper>
          </Box>
        )}
      </Box>
    </Header>
  );
};
